import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { selectUserPermissionMap, selectUserProfile } from 'store/features/authSlice';
import { setup } from '@icp/settings';
import restApi from 'services/restApi';
import IcpApp, { useAppContext } from '@icp/app';
import store from './store';
import AuthWrapper from './components/PageRouter/AuthWrapper';
import SSOCallback from './components/PageRouter/SSOCallback';
import SSOCallback2 from './components/PageRouter/SSOCallback2';
import OpAuthorize from './components/PageRouter/OpAuthorize';
import CurrentPageFormStoreProvider from './CurrentPageFormStoreProvider';
import SaleConsultationAI from './customerFeatures/jcfw/SaleConsultationAI';
import JobSuggestionAIAgent from './customerFeatures/jobsuggestion/JobSuggestionAIAgent';
import PDFChatAI from './customerFeatures/pdfchat';
import GeneralAIAgent from './GeneralAIAgent';
import DemoChat from './components/DemoChat';
import Chat from './components/Chat';
import PublicChat from './components/PublicChat';
import PersonalDashboard from './components/Dashboard/PersonalDashboard';
import AgentHome from './components/AgentHome';
import * as extensionComponents from './extensionComponents';
import MobileGeneralAIAgentPage from './components/MobileGeneralAIAgentPage/MobileGeneralAIAgentPage';

setup({ restApi, extensionComponents });

const otherRoutes = [
  {
    noNeedAuth: true,
    path: '/login/sso-callback/:loginSourceToken',
    element: <SSOCallback />,
  },
  {
    noNeedAuth: true,
    path: '/login/sso-callback2',
    element: <SSOCallback2 />,
  },
  {
    noNeedAuth: true,
    path: '/login/op/authorize',
    element: <OpAuthorize />,
  },
  window.__bot_conf__.enableAI
    ? {
        path: '/chat',
        element: <DemoChat />,
      }
    : null,
  window.__bot_conf__.enableAI
    ? {
        path: '/ai-chat',
        element: <Chat />,
      }
    : null,
  window.__bot_conf__.enableAI
    ? {
        path: '/sale-consultation-ai',
        element: <SaleConsultationAI />,
      }
    : null,
  {
    path: '/personal-dashboard',
    element: <PersonalDashboard />,
  },
  {
    path: '/agent-home',
    element: <AgentHome />,
  },
  {
    path: '/mobile/personal-dashboard',
    element: <PersonalDashboard />,
  },
  window.__bot_conf__.enableAI
    ? {
        noNeedAuth: true,
        path: '/pdf-chat',
        element: <PDFChatAI />,
      }
    : null,
  window.__bot_conf__.enableAI
    ? {
        noNeedAuth: true,
        path: '/external',
        children: [
          {
            path: 'chat',
            element: <PublicChat />,
          },
        ],
      }
    : null,
  window.__bot_conf__.enableAI
    ? {
        path: '/mobile/system-ai-agent',
        element: <MobileGeneralAIAgentPage />,
      }
    : null,
].filter(Boolean);

function AppChildren() {
  const userProfile = useSelector(selectUserProfile);
  const context = useAppContext();

  if (!userProfile) return null;

  const systemAgentPbc = context?.pbcList?.find(
    (pbc) => pbc.token === 'system-agent' || pbc.token.startsWith('system-agent-'),
  );
  if (window.__bot_conf__.enableAI && systemAgentPbc) {
    return <GeneralAIAgent />;
  }

  const aiAgentConfPbc = context?.pbcList?.find((pbc) => pbc.token.startsWith('ai-agent-config-'));
  const aiAgentConfName = aiAgentConfPbc?.token?.replace('ai-agent-config-', '');
  if (window.__bot_conf__.enableAI && aiAgentConfName === JobSuggestionAIAgent.token) {
    return <JobSuggestionAIAgent userProfile={userProfile} />;
  }

  return null;
}

function App() {
  const userPermissionMap = useSelector(selectUserPermissionMap);
  const userProfile = useSelector(selectUserProfile);

  return (
    <IcpApp
      context={{ userPermissionMap, userProfile }}
      AuthWrapper={AuthWrapper}
      // otherRoutes 里除了 noNeedAuth 都是 react router 的属性,
      // https://reactrouter.com/en/main/route/route
      otherRoutes={otherRoutes}
    >
      <CurrentPageFormStoreProvider>
        <AppChildren />
      </CurrentPageFormStoreProvider>
    </IcpApp>
  );
}

function AppWrap() {
  return (
    <ReduxProvider store={store}>
      <App />
    </ReduxProvider>
  );
}

export default AppWrap;
